import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { SiteFooter } from "@/components/site-footer";
import { detectSeoLocale } from "@/lib/seo-locale";
import { seoMeta, SITE_URL, DEFAULT_SEO_LOCALE } from "@/lib/seo";
import { safeNextPath } from "@/lib/auth-redirect";

export const Route = createFileRoute("/auth")({
  ssr: false,
  validateSearch: (search: Record<string, unknown>) => {
    const next = safeNextPath(search["next"]);
    return next ? { next } : {};
  },
  loader: async () => ({ lang: await detectSeoLocale() }),
  head: ({ loaderData }) => ({
    meta: seoMeta("auth", loaderData?.lang ?? DEFAULT_SEO_LOCALE, {
      path: "/auth",
      robots: "noindex,follow",
    }),
    links: [{ rel: "canonical", href: `${SITE_URL}/auth` }],
  }),
  component: AuthPage,
});

function AuthPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { next } = Route.useSearch();
  const afterLogin = () => navigate({ to: (next ?? "/") as string });
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) afterLogin();
    });
  }, [navigate]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "signup") {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: window.location.origin,
            data: { full_name: fullName },
          },
        });
        if (error) throw error;
        if (!data.session) {
          toast.success(t("auth.toasts.checkEmail"));
          setMode("signin");
          return;
        }
        toast.success(t("auth.toasts.accountCreated"));
        navigate({ to: "/onboarding" });
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        afterLogin();
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("auth.toasts.error"));
    } finally {
      setLoading(false);
    }
  }

  async function forgotPassword() {
    if (!email.trim()) {
      toast.error(t("auth.toasts.emailFirst"));
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      toast.success(t("auth.toasts.resetSent"));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("auth.toasts.error"));
    } finally {
      setLoading(false);
    }
  }


  async function google() {
    setGoogleLoading(true);
    try {
      const res = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: window.location.origin,
      });
      if ("error" in res && res.error) throw res.error;
      // Session is set by the broker; onAuthStateChange in root will redirect
      const { data } = await supabase.auth.getSession();
      if (data.session) afterLogin();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("auth.toasts.googleError"));
    } finally {
      setGoogleLoading(false);
    }
  }

  async function apple() {
    setAppleLoading(true);
    try {
      const res = await lovable.auth.signInWithOAuth("apple", {
        redirect_uri: window.location.origin,
      });
      if ("error" in res && res.error) throw res.error;
      const { data } = await supabase.auth.getSession();
      if (data.session) afterLogin();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("auth.toasts.appleError"));
    } finally {
      setAppleLoading(false);
    }
  }

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-background px-[max(env(safe-area-inset-left),1.25rem)] pr-[max(env(safe-area-inset-right),1.25rem)] pt-[max(env(safe-area-inset-top),1rem)] pb-[max(env(safe-area-inset-bottom),1rem)]">
      <div className="w-full max-w-sm py-8">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-[var(--shadow-soft)]">
            <Sparkles className="h-7 w-7" />
          </div>
          <h1 className="text-3xl font-semibold tracking-tight">{t("auth.heroTitle")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("auth.heroSubtitle")}
          </p>
        </div>

        <Card className="card-soft border-0 p-6">
          <Button
            type="button"
            variant="outline"
            className="mb-4 w-full rounded-xl"
            onClick={google}
            disabled={googleLoading || appleLoading || loading}
          >
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" aria-hidden>
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.12A6.98 6.98 0 0 1 5.47 12c0-.74.13-1.45.36-2.12V7.04H2.18A11 11 0 0 0 1 12c0 1.77.42 3.44 1.18 4.96l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.04l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
            </svg>
            {googleLoading ? t("auth.pleaseWait") : t("auth.continueWithGoogle")}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="mb-4 w-full rounded-xl"
            onClick={apple}
            disabled={googleLoading || appleLoading || loading}
          >
            <svg className="mr-2 h-4 w-4 fill-current" viewBox="0 0 24 24" aria-hidden>
              <path d="M16.36 12.83c.02 2.5 2.19 3.33 2.22 3.35-.02.06-.35 1.2-1.14 2.37-.69 1.02-1.4 2.03-2.53 2.05-1.11.02-1.47-.66-2.74-.66-1.27 0-1.66.64-2.71.68-1.09.04-1.92-1.1-2.61-2.11-1.42-2.06-2.5-5.83-1.05-8.37.72-1.27 2.02-2.07 3.42-2.09 1.07-.02 2.08.72 2.74.72.65 0 1.88-.89 3.17-.76.54.02 2.06.22 3.03 1.65-.08.05-1.81 1.06-1.8 3.17zM14.3 5.2c.59-.72.99-1.72.88-2.71-.85.03-1.88.57-2.49 1.28-.55.63-1.03 1.65-.9 2.62.95.07 1.92-.48 2.51-1.19z" />
            </svg>
            {appleLoading ? t("auth.pleaseWait") : t("auth.continueWithApple")}
          </Button>
          <div className="mb-4 flex items-center gap-3 text-xs uppercase tracking-wider text-muted-foreground">
            <span className="h-px flex-1 bg-border" /> {t("auth.orEmail")} <span className="h-px flex-1 bg-border" />
          </div>
          <form onSubmit={submit} className="space-y-4">
            {mode === "signup" && (
              <div className="space-y-1.5">
                <Label htmlFor="name">{t("auth.nameLabel")}</Label>
                <Input id="name" value={fullName} onChange={(e) => setFullName(e.target.value)} required />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">{t("auth.emailLabel")}</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pw">{t("auth.passwordLabel")}</Label>
              <Input id="pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} autoComplete={mode === "signup" ? "new-password" : "current-password"} />
            </div>
            <Button type="submit" className="w-full rounded-xl" disabled={loading}>
              {loading ? t("auth.pleaseWait") : mode === "signup" ? t("auth.createAccount") : t("auth.signIn")}
            </Button>
          </form>
          {mode === "signin" && (
            <button
              type="button"
              onClick={forgotPassword}
              disabled={loading}
              className="mt-3 block w-full text-center text-sm text-primary hover:underline"
            >
              {t("auth.forgotPassword")}
            </button>
          )}
          <button
            type="button"
            onClick={() => setMode(mode === "signup" ? "signin" : "signup")}
            className="mt-4 block w-full text-center text-sm text-muted-foreground hover:text-foreground"
          >
            {mode === "signup" ? t("auth.haveAccount") : t("auth.noAccount")}
          </button>
          <Link
            to="/parent-auth"
            className="mt-3 block w-full text-center text-sm text-muted-foreground hover:text-foreground"
          >
            Sei un genitore? Accedi all'area famiglia
          </Link>

        </Card>
      </div>
      <SiteFooter />
    </div>
  );
}
